<template>
  <div class="prompt-lab agent-lab-shell">
    <div class="agent-hero">
      <p class="hero-hint">像聊天一样描述目标，Agent 会边理解、边追问、边展示真实测试效果；你不回应时自动继续。</p>
      <div class="hero-pulse" :class="{ active: running }">
        <span></span>
        <strong>{{ agentMood }}</strong>
        <em v-if="job" class="live-flag" :class="{ live: wsConnected }">{{ wsConnected ? '实时' : '离线兜底' }}</em>
      </div>
    </div>

    <el-row :gutter="18" class="agent-layout">
      <el-col :lg="15" :md="24">
        <el-card class="conversation-panel" shadow="never">
          <div ref="chatScrollRef" class="chat-scroll">
            <transition-group name="message-flow" tag="div" class="message-stack">
              <div
                v-for="message in agentMessages"
                :key="message.id"
                class="chat-message"
                :class="[`role-${message.role}`, message.kind, { pending: message.pending }]"
              >
                <div class="avatar">{{ message.role === 'user' ? '你' : 'A' }}</div>
                <div class="bubble">
                  <div class="bubble-head">
                    <strong>{{ message.title }}</strong>
                    <span>{{ message.time }}</span>
                  </div>
                  <p>{{ message.message }}</p>

                  <div v-if="message.pending" class="inline-question">
                    <div>
                      <b>我现在等你感觉一下。</b>
                      <span>{{ countdownText }} 后默认继续：{{ pendingEvent.default_action }}</span>
                    </div>
                    <div class="quick-actions">
                      <el-button type="primary" size="small" @click="sendIntervention('continue', '这个阶段确认继续')">继续</el-button>
                      <el-button size="small" @click="sendQuickFeeling('方向是对的，继续加强这个感觉')">方向对</el-button>
                      <el-button size="small" @click="sendIntervention('reset_direction', '这个方向不对，推倒重来')">感觉不对</el-button>
                      <el-button type="success" size="small" @click="sendIntervention('early_accept', '我现在提前收货')">先收货</el-button>
                    </div>
                  </div>

                  <div v-if="message.preview?.type === 'spec'" class="message-preview spec-preview">
                    <div class="preview-row" v-if="spec?.analysis">
                      <span>我的理解</span>
                      <p>{{ spec.analysis }}</p>
                    </div>
                    <div class="preview-row" v-else>
                      <span>用途</span>
                      <p>{{ spec?.purpose }}</p>
                    </div>
                    <div class="spec-modules" v-if="spec?.spec_modules?.length">
                      <div v-for="mod in spec.spec_modules" :key="mod.key" class="spec-module-mini">
                        <div class="spec-module-mini-head">
                          <strong>{{ mod.title }}</strong>
                          <em v-if="mod.summary">{{ mod.summary }}</em>
                        </div>
                        <ul>
                          <li v-for="(item, idx) in mod.items" :key="idx" :class="`sev-${item.severity}`">
                            <el-tag size="small" :type="severityTagType(item.severity)">{{ severityLabel(item.severity) }}</el-tag>
                            <span>{{ item.text }}</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                    <div class="preview-row" v-if="spec?.open_questions?.length">
                      <span>想追问你</span>
                      <ul class="open-questions">
                        <li v-for="q in spec.open_questions" :key="q">{{ q }}</li>
                      </ul>
                    </div>
                  </div>

                  <div v-if="message.preview?.type === 'candidate' && bestCandidate" class="message-preview prompt-preview">
                    <div class="prompt-preview-head">
                      <span>{{ bestCandidate.generation_strategy }}</span>
                      <el-tag type="success" size="small">{{ bestCandidate.score || 0 }} 分</el-tag>
                    </div>
                    <pre>{{ compactPrompt(bestCandidate.prompt_text) }}</pre>
                  </div>

                  <div v-if="message.preview?.type === 'run' && sampleRuns.length" class="message-preview run-preview">
                    <div v-for="run in sampleRuns.slice(0, 2)" :key="run.id" class="run-card-mini">
                      <strong>{{ run.success ? `评分 ${run.judge?.score ?? '-'}` : '调用失败' }}</strong>
                      <span>{{ run.success ? compactOutput(run.output) : run.error }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </transition-group>
            <div v-if="liveStream.text" class="chat-message role-agent streaming-live">
              <div class="avatar">A</div>
              <div class="bubble stream-bubble">
                <div class="bubble-head">
                  <strong>{{ liveStream.agent || 'Agent' }} · 进行中</strong>
                  <span>实时</span>
                </div>
                <pre class="stream-text">{{ liveStream.text }}</pre>
              </div>
            </div>
          </div>

          <div class="composer-card" :class="{ active: job }">
            <template v-if="!job">
              <el-input
                v-model="form.purpose"
                type="textarea"
                :rows="6"
                resize="none"
                placeholder="直接说你的需求就行；如果有喜欢的输出样子，也可以贴在同一段里，我会一起理解。"
                @keydown.ctrl.enter.prevent="startJob"
              />
              <div class="starter-options">
                <div class="settings-line">
                  <el-select v-model="form.mode" size="small">
                    <el-option label="快速模式" value="quick" />
                    <el-option label="深度模式" value="deep" />
                  </el-select>
                  <el-input v-model="form.target_model" size="small" :placeholder="`目标模型：默认 ${defaultTargetModel}`" />
                </div>
              </div>
              <div class="composer-actions">
                <span>Ctrl + Enter 也可以开始</span>
                <el-button type="primary" size="large" :loading="running" @click="startJob">让 Agent 开始工作</el-button>
              </div>
            </template>

            <template v-else>
              <div class="feedback-bar">
                <el-input
                  v-model="feedbackText"
                  type="textarea"
                  :rows="2"
                  resize="none"
                  placeholder="随时插一句你的感觉：更口语、更像真人、不要标题党、这个方向不对……"
                  @keydown.ctrl.enter.prevent="submitFeedback"
                />
                <el-button type="primary" :disabled="!feedbackText.trim()" @click="submitFeedback">发给 Agent</el-button>
              </div>
              <div class="composer-chips">
                <el-button size="small" @click="sendQuickFeeling('更像真人一点，减少模板感')">更拟人</el-button>
                <el-button size="small" @click="sendQuickFeeling('保留小红书感觉，但不要太广告')">少点广告</el-button>
                <el-button size="small" @click="sendQuickFeeling('更贴近我给的示例风格')">贴近示例</el-button>
                <el-button size="small" @click="refreshJob">刷新</el-button>
              </div>
            </template>
          </div>
        </el-card>
      </el-col>

      <el-col :lg="9" :md="24">
        <div class="side-rail">
          <el-card class="panel status-card" v-if="job" shadow="never">
            <div class="status-topline">
              <div>
                <span>Agent 状态</span>
                <strong>{{ statusLabel }}</strong>
              </div>
              <el-tag :type="pendingEvent ? 'warning' : 'success'">{{ pendingEvent ? '等你感觉' : '自动推进中' }}</el-tag>
            </div>
            <div class="status-grid compact-status">
              <div class="metric">
                <span>模型调用</span>
                <strong>{{ ledger.model_calls || 0 }} / {{ budget.max_model_calls || '-' }}</strong>
              </div>
              <div class="metric">
                <span>迭代</span>
                <strong>{{ blackboard.current_iteration || 0 }} / {{ budget.max_iterations || '-' }}</strong>
              </div>
              <div class="metric">
                <span>当前最佳</span>
                <strong>{{ bestScore }}</strong>
              </div>
            </div>
          </el-card>

          <el-card v-if="modelError" class="panel model-error-alert" shadow="never">
            <el-alert type="error" :closable="false" show-icon title="目标模型调用失败">
              {{ modelError }}
            </el-alert>
          </el-card>

          <el-card v-if="bestCandidate" class="panel best-card" shadow="never">
            <template #header>
              <div class="panel-header compact">
                <h3>当前最优</h3>
                <el-tag type="success">{{ bestCandidate.score || 0 }} 分</el-tag>
              </div>
            </template>
            <p>{{ bestCandidate.generation_strategy }}</p>
            <div class="intervention-bar left">
              <el-button size="small" type="primary" @click="sendIntervention('prefer_candidate', '我更喜欢这个候选方向', { candidate_id: bestCandidate.id })">喜欢方向</el-button>
              <el-button size="small" @click="openManualPrompt">手改一版</el-button>
              <el-button size="small" type="success" @click="sendIntervention('early_accept', '我决定提前收货')">收货</el-button>
            </div>
          </el-card>

          <el-card v-if="job" class="panel detail-card" shadow="never">
            <template #header>
              <div class="panel-header compact">
                <h3>工作台详情</h3>
                <el-tag>可展开</el-tag>
              </div>
            </template>
            <el-collapse v-model="openPanels">
              <el-collapse-item v-if="spec" :title="`需求规格清单（${spec.generated_by === 'llm' ? 'AI 拆解' : '未生成'}）`" name="spec">
                <p v-if="spec.compilation_note" class="notice">{{ spec.compilation_note }}</p>
                <p v-if="spec.analysis" class="notice analysis-line">{{ spec.analysis }}</p>
                <div class="spec-modules-full" v-if="spec.spec_modules?.length">
                  <div v-for="mod in spec.spec_modules" :key="mod.key" class="spec-module-full">
                    <div class="spec-module-full-head">
                      <strong>{{ mod.title }}</strong>
                      <span v-if="mod.summary">{{ mod.summary }}</span>
                    </div>
                    <ul>
                      <li v-for="(item, idx) in mod.items" :key="idx">
                        <el-tag :type="severityTagType(item.severity)" size="small">{{ severityLabel(item.severity) }}</el-tag>
                        <span class="item-text">{{ item.text }}</span>
                        <em v-if="item.rationale" class="item-rationale">— {{ item.rationale }}</em>
                        <em v-if="item.source === 'inferred'" class="item-source">推断</em>
                        <em v-else-if="item.source === 'example'" class="item-source">来自示例</em>
                      </li>
                    </ul>
                  </div>
                </div>
                <p v-else class="notice muted">这次没有产出规格清单，Agent 会直接拿你的原话继续。</p>
                <div v-if="spec.open_questions?.length" class="open-questions-block">
                  <strong>Agent 想追问：</strong>
                  <ul>
                    <li v-for="q in spec.open_questions" :key="q">{{ q }}</li>
                  </ul>
                </div>
                <div v-if="spec.conflicts?.length" class="conflicts-block">
                  <strong>疑似冲突：</strong>
                  <ul>
                    <li v-for="c in spec.conflicts" :key="c">{{ c }}</li>
                  </ul>
                </div>
                <div class="spec-footline">
                  <span>目标模型：{{ spec.model_policy.test_runner_provider }} / {{ spec.model_policy.test_runner_model }}</span>
                </div>
              </el-collapse-item>

              <el-collapse-item v-if="bestCandidate" title="提示词全文" name="prompt">
                <el-input :model-value="bestCandidate.prompt_text" type="textarea" :rows="14" readonly />
                <div class="intervention-bar">
                  <el-button size="small" @click="sendIntervention('reject_candidate', '砍掉这个候选', { candidate_id: bestCandidate.id })">砍掉</el-button>
                  <el-button size="small" type="primary" @click="sendIntervention('prefer_candidate', '我更喜欢这个候选方向', { candidate_id: bestCandidate.id })">偏好</el-button>
                </div>
              </el-collapse-item>

              <el-collapse-item v-if="sampleRuns.length" title="真实测试输出" name="runs">
                <el-collapse accordion>
                  <el-collapse-item v-for="run in sampleRuns" :key="run.id" :title="runTitle(run)">
                    <el-alert v-if="!run.success" type="error" :closable="false" title="目标模型运行失败">
                      {{ run.error || '未返回有效输出' }}
                    </el-alert>
                    <pre v-if="run.output" class="output-box">{{ run.output }}</pre>
                    <pre v-else class="output-box empty-output">（无输出）</pre>
                    <el-alert v-if="run.judge" type="info" :closable="false" :title="`裁判评分：${run.judge.score}`">
                      {{ run.judge.summary }}
                    </el-alert>
                    <div class="run-actions">
                      <el-button size="small" type="success" @click="sendIntervention('like_output', '这个输出我喜欢', { candidate_id: run.candidate_id, payload: { run_id: run.id } })">喜欢</el-button>
                      <el-button size="small" type="danger" @click="sendIntervention('dislike_output', '这个输出不喜欢', { candidate_id: run.candidate_id, payload: { run_id: run.id } })">不喜欢</el-button>
                    </div>
                  </el-collapse-item>
                </el-collapse>
              </el-collapse-item>

              <el-collapse-item v-if="delivery" title="交付报告" name="delivery">
                <p>{{ delivery.summary }}</p>
                <p class="notice">{{ delivery.model_notice }}</p>
                <el-alert type="warning" :closable="false" title="差距报告">
                  <p>{{ delivery.gap_report }}</p>
                  <p v-for="w in delivery.known_weaknesses" :key="w">{{ w }}</p>
                </el-alert>
              </el-collapse-item>
            </el-collapse>
          </el-card>
        </div>
      </el-col>
    </el-row>

    <el-dialog v-model="manualDialogVisible" title="手动改一版提示词" width="760px">
      <el-input v-model="manualPrompt" type="textarea" :rows="16" />
      <template #footer>
        <el-button @click="manualDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitManualPrompt">加入候选池</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import { ElMessage } from 'element-plus'
import { promptLabApi } from '../api'

export default {
  name: 'PromptLab',
  setup() {
    const form = reactive({
      purpose: '',
      expected_example: '',
      hard_requirements: '',
      forbidden: '',
      target_provider: 'openrouter',
      target_model: '',
      optimizer_provider: 'openrouter',
      optimizer_model: '',
      mode: 'quick',
      demo_variables: {}
    })
    const demoTopic = ref('')
    const job = ref(null)
    const running = ref(false)
    const timer = ref(null)
    const clockTimer = ref(null)
    const ws = ref(null)
    const wsConnected = ref(false)
    const wsReconnectTimer = ref(null)
    const now = ref(Date.now())
    const manualDialogVisible = ref(false)
    const manualPrompt = ref('')
    const defaultTargetModel = ref('z-ai/glm-5v-turbo')
    const feedbackText = ref('')
    const chatScrollRef = ref(null)
    const openPanels = ref(['spec'])
    const liveStream = ref({ agent: '', channel: '', text: '' })

    const blackboard = computed(() => job.value?.blackboard || {})
    const spec = computed(() => blackboard.value.requirement_spec)
    const events = computed(() => blackboard.value.events || [])
    const ledger = computed(() => blackboard.value.budget_ledger || {})
    const budget = computed(() => blackboard.value.budget_policy || {})
    const delivery = computed(() => blackboard.value.delivery)
    const statusLabel = computed(() => job.value?.status || '-')
    const pendingEvent = computed(() => {
      const pendingId = blackboard.value.pending_event_id
      return (events.value || []).find((e) => e.id === pendingId) || (events.value || []).find((e) => e.requires_ack && !e.acknowledged) || null
    })
    const countdownText = computed(() => {
      if (!pendingEvent.value?.auto_continue_at) return '稍后'
      const left = Math.max(0, Math.ceil((new Date(pendingEvent.value.auto_continue_at).getTime() - now.value) / 1000))
      return `${left}s`
    })
    const candidates = computed(() => blackboard.value.candidates || [])
    const bestCandidate = computed(() => {
      return [...candidates.value].sort((a, b) => (b.score || 0) - (a.score || 0))[0]
    })
    const bestScore = computed(() => bestCandidate.value ? `${bestCandidate.value.score || 0}` : '-')
    const sampleRuns = computed(() => {
      const bestId = bestCandidate.value?.id
      return (blackboard.value.test_runs || []).filter((r) => r.candidate_id === bestId).slice(0, 4)
    })
    const modelError = computed(() => {
      const failed = (blackboard.value.test_runs || []).find((r) => !r.success && r.error)
      if (failed?.error) return failed.error
      const errEvent = [...(events.value || [])].reverse().find((e) => e.kind === 'error' && e.payload?.error)
      return errEvent?.payload?.error || ''
    })
    const agentMood = computed(() => {
      if (!job.value) return '等你开口'
      if (pendingEvent.value) return '等你反馈'
      if (running.value) return '正在推理'
      if (delivery.value) return '已交付'
      return '已暂停'
    })

    const formatTime = (value) => {
      if (!value) return ''
      return new Date(value).toLocaleTimeString()
    }

    const eventPreview = (event) => {
      if (event.payload?.requirement_spec) return { type: 'spec' }
      if (event.payload?.candidates || event.payload?.new_candidate) return { type: 'candidate' }
      if (event.payload?.sample_runs?.length || event.title?.includes('测试结果')) return { type: 'run' }
      return null
    }

    const agentMessages = computed(() => {
      const messages = []
      if (!job.value) {
        messages.push({
          id: 'welcome',
          role: 'agent',
          kind: 'welcome',
          title: '你直接说需求就行',
          message: '不用先想好完整提示词。告诉我用途，最好再贴一个你喜欢的输出样子，我会先复述理解，再问你感觉对不对。',
          time: '现在'
        })
        return messages
      }
      messages.push({
        id: 'initial-user',
        role: 'user',
        kind: 'user-input',
        title: '我的需求',
        message: blackboard.value.requirement_input?.purpose || form.purpose,
        time: formatTime(blackboard.value.budget_ledger?.started_at)
      })
      for (const event of events.value) {
        const isUser = event.kind === 'user_intervention'
        messages.push({
          id: event.id,
          role: isUser ? 'user' : 'agent',
          kind: event.kind,
          title: event.title,
          message: event.message,
          time: formatTime(event.created_at),
          pending: pendingEvent.value?.id === event.id,
          preview: eventPreview(event),
          event
        })
      }
      return messages
    })

    const scrollToBottom = async () => {
      await nextTick()
      const el = chatScrollRef.value
      if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' })
    }

    watch(() => agentMessages.value.length, scrollToBottom)
    watch(pendingEvent, scrollToBottom)

    onMounted(async () => {
      try {
        const defaults = await promptLabApi.getDefaults()
        if (defaults?.default_target_model) defaultTargetModel.value = defaults.default_target_model
      } catch (_) {
        // ignore
      }
      clockTimer.value = setInterval(() => { now.value = Date.now() }, 1000)
    })

    const refreshJob = async () => {
      if (!job.value?.job_id) return
      job.value = await promptLabApi.getJob(job.value.job_id)
      if (['delivered', 'fused', 'failed', 'cancelled'].includes(job.value.status)) {
        running.value = false
        if (timer.value) clearInterval(timer.value)
      }
    }

    const stopPolling = () => {
      if (timer.value) {
        clearInterval(timer.value)
        timer.value = null
      }
    }

    const startPolling = () => {
      stopPolling()
      timer.value = setInterval(refreshJob, 1600)
    }

    const closeWebSocket = () => {
      if (wsReconnectTimer.value) {
        clearTimeout(wsReconnectTimer.value)
        wsReconnectTimer.value = null
      }
      if (ws.value) {
        try { ws.value.close() } catch (_) {}
        ws.value = null
      }
      wsConnected.value = false
    }

    const applyIncoming = (payload) => {
      if (!payload || !job.value) return
      if (payload.type === 'ping') return
      if (payload.type === 'snapshot' && payload.blackboard) {
        job.value = {
          job_id: payload.job_id,
          status: payload.status,
          blackboard: payload.blackboard
        }
      } else if (payload.type === 'event' && payload.event) {
        const bb = job.value.blackboard || {}
        const events = Array.isArray(bb.events) ? bb.events : []
        const idx = events.findIndex((e) => e.id === payload.event.id)
        if (idx >= 0) {
          events.splice(idx, 1, payload.event)
        } else {
          events.push(payload.event)
        }
        job.value = {
          ...job.value,
          status: payload.status || job.value.status,
          blackboard: {
            ...bb,
            events,
            pending_event_id: payload.pending_event_id ?? bb.pending_event_id
          }
        }
      } else if (payload.type === 'stream') {
        if (payload.done && payload.channel !== 'token') {
          setTimeout(() => {
            liveStream.value = { agent: '', channel: '', text: '' }
          }, 600)
        } else if (payload.delta) {
          const ch = payload.channel || 'token'
          if (liveStream.value.agent !== payload.agent || liveStream.value.channel !== ch) {
            liveStream.value = { agent: payload.agent || 'agent', channel: ch, text: payload.delta }
          } else {
            liveStream.value.text += payload.delta
          }
          scrollToBottom()
        }
        return
      } else if (payload.type === 'error') {
        ElMessage.error(payload.message || 'WebSocket 报错')
        return
      } else {
        return
      }
      if (['delivered', 'fused', 'failed', 'cancelled'].includes(job.value.status)) {
        running.value = false
        closeWebSocket()
        stopPolling()
      }
    }

    const connectWebSocket = (jobId) => {
      if (!jobId) return
      closeWebSocket()
      let socket
      try {
        socket = promptLabApi.openWebSocket(jobId)
      } catch (error) {
        startPolling()
        return
      }
      ws.value = socket
      socket.onopen = () => {
        wsConnected.value = true
        stopPolling()
      }
      socket.onmessage = (event) => {
        try {
          const payload = JSON.parse(event.data)
          applyIncoming(payload)
        } catch (_) {
          // ignore
        }
      }
      socket.onerror = () => {
        wsConnected.value = false
      }
      socket.onclose = () => {
        wsConnected.value = false
        if (running.value && job.value?.job_id) {
          startPolling()
          if (wsReconnectTimer.value) clearTimeout(wsReconnectTimer.value)
          wsReconnectTimer.value = setTimeout(() => connectWebSocket(job.value.job_id), 2500)
        }
      }
    }

    const startJob = async () => {
      if (!form.purpose.trim()) {
        ElMessage.warning('先写一句你的需求')
        return
      }
      running.value = true
      form.demo_variables = demoTopic.value.trim() ? { 主题: demoTopic.value.trim() } : {}
      try {
        job.value = await promptLabApi.createJob({ ...form })
        ElMessage.success('Agent 已开始工作，你可以随时插话')
        connectWebSocket(job.value.job_id)
        scrollToBottom()
      } catch (error) {
        running.value = false
        ElMessage.error(error.message || '启动失败')
      }
    }

    const sendIntervention = async (type, message, extra = {}) => {
      if (!job.value?.job_id) return
      job.value = await promptLabApi.intervene(job.value.job_id, { type, message, ...extra })
      running.value = !['delivered', 'fused', 'failed', 'cancelled'].includes(job.value.status)
      if (!wsConnected.value && running.value) {
        connectWebSocket(job.value.job_id)
      }
      scrollToBottom()
    }

    const submitFeedback = async () => {
      const text = feedbackText.value.trim()
      if (!text) return
      feedbackText.value = ''
      await sendIntervention('requirement_update', text)
      ElMessage.success('已告诉 Agent，它会按你的感觉调整')
    }

    const sendQuickFeeling = async (text) => {
      feedbackText.value = ''
      const reset = /不对|错了|重来|方向|全错|推倒/.test(text)
      await sendIntervention(reset ? 'reset_direction' : 'requirement_update', text)
    }

    const openManualPrompt = () => {
      manualPrompt.value = bestCandidate.value?.prompt_text || ''
      manualDialogVisible.value = true
    }

    const submitManualPrompt = async () => {
      await sendIntervention('manual_prompt', '用户手动改了一版提示词', { prompt_text: manualPrompt.value })
      manualDialogVisible.value = false
    }

    const runTitle = (run) => {
      if (!run.success) return `${run.model_provider}/${run.model_name} · 调用失败`
      return `${run.model_provider}/${run.model_name} · ${run.judge ? `评分 ${run.judge.score}` : '待评'}`
    }

    const compactPrompt = (text) => {
      const value = text || ''
      return value.length > 520 ? `${value.slice(0, 520)}\n...` : value
    }

    const compactOutput = (text) => {
      const value = (text || '').replace(/\s+/g, ' ').trim()
      return value.length > 160 ? `${value.slice(0, 160)}...` : value || '无输出'
    }

    const severityLabel = (sev) => {
      const map = { must: '必须', should: '建议', avoid: '避免', note: '备注' }
      return map[sev] || '建议'
    }

    const severityTagType = (sev) => {
      const map = { must: 'danger', should: 'primary', avoid: 'warning', note: 'info' }
      return map[sev] || 'primary'
    }

    onBeforeUnmount(() => {
      if (timer.value) clearInterval(timer.value)
      if (clockTimer.value) clearInterval(clockTimer.value)
      closeWebSocket()
    })

    return {
      form,
      demoTopic,
      job,
      running,
      blackboard,
      spec,
      events,
      ledger,
      budget,
      delivery,
      statusLabel,
      bestCandidate,
      bestScore,
      sampleRuns,
      pendingEvent,
      countdownText,
      modelError,
      defaultTargetModel,
      feedbackText,
      chatScrollRef,
      openPanels,
      agentMood,
      agentMessages,
      wsConnected,
      liveStream,
      manualDialogVisible,
      manualPrompt,
      startJob,
      refreshJob,
      sendIntervention,
      submitFeedback,
      sendQuickFeeling,
      openManualPrompt,
      submitManualPrompt,
      runTitle,
      compactPrompt,
      compactOutput,
      severityLabel,
      severityTagType
    }
  }
}
</script>

<style scoped>
.prompt-lab {
  min-height: 100%;
}

.agent-lab-shell {
  padding-bottom: 28px;
}

.agent-hero {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px 18px;
  flex-wrap: wrap;
  margin-bottom: 14px;
}

.hero-hint {
  margin: 0;
  font-size: 12px;
  color: var(--mp-text-secondary);
}




.hero-pulse {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 6px 14px;
  border-radius: 999px;
  background: #ffffff;
  font-size: 12px;
  color: #0f172a;
  border: 1px solid #dbeafe;
}

.hero-pulse span {
  width: 10px;
  height: 10px;
  border-radius: 999px;
  background: #94a3b8;
}

.hero-pulse.active span {
  background: #22c55e;
  box-shadow: 0 0 0 8px rgba(34, 197, 94, 0.12);
  animation: pulse-dot 1.4s infinite;
}

.live-flag {
  font-style: normal;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  color: #94a3b8;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
}

.live-flag.live {
  color: #059669;
  background: #ecfdf5;
  border-color: #a7f3d0;
}

.agent-layout {
  align-items: flex-start;
}

.conversation-panel {
  overflow: hidden;
  border-radius: 22px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 20px 60px rgba(15, 23, 42, 0.08);
}

.chat-scroll {
  height: calc(100vh - 310px);
  min-height: 520px;
  overflow: auto;
  padding: 22px 22px 8px;
  scroll-behavior: smooth;
  background: linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
}

.message-stack {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.chat-message {
  display: flex;
  gap: 12px;
  align-items: flex-start;
}

.chat-message.role-user {
  flex-direction: row-reverse;
}

.avatar {
  flex: 0 0 38px;
  width: 38px;
  height: 38px;
  border-radius: 14px;
  display: grid;
  place-items: center;
  color: #ffffff;
  font-weight: 800;
  background: linear-gradient(135deg, #2563eb, #7c3aed);
  box-shadow: 0 10px 24px rgba(37, 99, 235, 0.24);
}

.role-user .avatar {
  background: linear-gradient(135deg, #0f172a, #334155);
}

.bubble {
  max-width: min(720px, 82%);
  padding: 15px 16px;
  border-radius: 18px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  box-shadow: 0 10px 26px rgba(15, 23, 42, 0.06);
}

.role-user .bubble {
  background: #eff6ff;
  border-color: #bfdbfe;
}

.chat-message.pending .bubble {
  border-color: #f59e0b;
  box-shadow: 0 14px 36px rgba(245, 158, 11, 0.16);
}

.bubble-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  margin-bottom: 7px;
}

.bubble-head strong {
  color: #0f172a;
}

.bubble-head span {
  color: #94a3b8;
  font-size: 12px;
}

.bubble p {
  margin: 0;
  color: #475569;
  line-height: 1.75;
  white-space: pre-wrap;
}

.inline-question {
  margin-top: 12px;
  padding: 12px;
  border-radius: 14px;
  background: #fffbeb;
  border: 1px solid #fde68a;
}

.inline-question b,
.inline-question span {
  display: block;
}

.inline-question span {
  margin-top: 4px;
  color: #92400e;
  font-size: 13px;
}

.quick-actions,
.composer-chips,
.run-actions,
.intervention-bar {
  margin-top: 10px;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.message-preview {
  margin-top: 12px;
  padding: 12px;
  border-radius: 14px;
  background: #f8fafc;
  border: 1px solid #e5e7eb;
}

.preview-row {
  display: grid;
  grid-template-columns: 70px 1fr;
  gap: 10px;
  margin-bottom: 9px;
}

.preview-row:last-child {
  margin-bottom: 0;
}

.preview-row span {
  color: #64748b;
  font-size: 12px;
}

.preview-row p {
  margin: 0;
}

.spec-modules {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 8px;
}

.spec-module-mini {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 10px 12px;
  background: #f8fafc;
}

.spec-module-mini-head {
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 6px;
}

.spec-module-mini-head strong {
  color: #0f172a;
  font-size: 13px;
}

.spec-module-mini-head em {
  font-style: normal;
  color: #64748b;
  font-size: 12px;
}

.spec-module-mini ul,
.spec-module-full ul {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.spec-module-mini li,
.spec-module-full li {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 13px;
  color: #1f2937;
  line-height: 1.55;
}

.spec-module-mini li .el-tag,
.spec-module-full li .el-tag {
  flex-shrink: 0;
  margin-top: 2px;
}

.open-questions,
.open-questions-block ul,
.conflicts-block ul {
  margin: 4px 0 0;
  padding-left: 18px;
  color: #475569;
  font-size: 13px;
}

.spec-modules-full {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 10px;
}

.spec-module-full {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 12px 14px;
  background: #ffffff;
}

.spec-module-full-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;
}

.spec-module-full-head strong {
  color: #0f172a;
}

.spec-module-full-head span {
  color: #64748b;
  font-size: 12px;
}

.item-text {
  flex: 1;
}

.item-rationale {
  font-style: normal;
  color: #94a3b8;
  font-size: 12px;
}

.item-source {
  font-style: normal;
  color: #94a3b8;
  font-size: 11px;
  padding: 1px 6px;
  border-radius: 999px;
  background: #f1f5f9;
}

.analysis-line {
  color: #475569;
  font-size: 13px;
}

.notice.muted {
  color: #94a3b8;
}

.open-questions-block,
.conflicts-block {
  margin-top: 12px;
  font-size: 13px;
  color: #334155;
}

.spec-footline {
  margin-top: 10px;
  font-size: 12px;
  color: #64748b;
}

.prompt-preview-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.prompt-preview pre,
.output-box,
.example-box {
  white-space: pre-wrap;
  margin: 0;
  line-height: 1.65;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
}

.prompt-preview pre {
  max-height: 260px;
  overflow: auto;
  color: #334155;
}

.run-card-mini {
  padding: 10px 0;
  border-bottom: 1px solid #e5e7eb;
}

.run-card-mini:last-child {
  border-bottom: 0;
}

.run-card-mini strong,
.run-card-mini span {
  display: block;
}

.run-card-mini span {
  margin-top: 4px;
  color: #64748b;
  line-height: 1.6;
}

.composer-card {
  padding: 16px;
  border-top: 1px solid #e5e7eb;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(14px);
}

.starter-options {
  margin-top: 12px;
  display: grid;
  gap: 10px;
}

.settings-line {
  display: grid;
  grid-template-columns: 160px 1fr;
  gap: 10px;
}

.composer-actions {
  margin-top: 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}

.composer-actions span {
  color: #94a3b8;
  font-size: 13px;
}

.feedback-bar {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 10px;
  align-items: stretch;
}

.side-rail {
  position: sticky;
  top: 18px;
}

.panel {
  margin-bottom: 16px;
  border-radius: 18px;
  box-shadow: 0 12px 34px rgba(30, 64, 175, 0.06);
}

.status-card {
  background: linear-gradient(135deg, #eff6ff, #ffffff);
}

.status-topline {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}

.status-topline span,
.metric span {
  display: block;
  color: #64748b;
  font-size: 12px;
  margin-bottom: 5px;
}

.status-topline strong {
  display: block;
  color: #0f172a;
  font-size: 22px;
}

.status-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}

.metric {
  padding: 12px;
  border-radius: 14px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
}

.metric strong {
  color: #111827;
  font-size: 18px;
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.panel-header h3 {
  margin: 0;
  font-size: 17px;
}

.best-card p,
.notice {
  color: #64748b;
  line-height: 1.7;
}

.left {
  justify-content: flex-start;
}

.tag,
.score-tag {
  margin: 3px;
}

.example-box {
  margin-top: 8px;
  padding: 10px 12px;
  border-radius: 10px;
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  max-height: 220px;
  overflow: auto;
}

.output-box {
  padding: 14px;
  border-radius: 12px;
  background: #0f172a;
  color: #e5e7eb;
  max-height: 360px;
  overflow: auto;
}

.empty-output {
  color: #94a3b8;
  background: #1e293b;
}

.stream-text {
  margin: 0;
  white-space: pre-wrap;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 13px;
  line-height: 1.55;
  color: #334155;
  max-height: 220px;
  overflow: auto;
}

.streaming-live .stream-bubble {
  border: 1px dashed #93c5fd;
  background: #f8fbff;
  animation: pulse-soft 1.2s ease-in-out infinite;
}

@keyframes pulse-soft {
  0%, 100% { box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.08); }
  50% { box-shadow: 0 0 0 6px rgba(59, 130, 246, 0.04); }
}

.message-flow-enter-active,
.message-flow-leave-active {
  transition: all 0.32s ease;
}

.message-flow-enter-from {
  opacity: 0;
  transform: translateY(12px) scale(0.98);
}

.message-flow-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

@keyframes pulse-dot {
  0% { box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.28); }
  70% { box-shadow: 0 0 0 12px rgba(34, 197, 94, 0); }
  100% { box-shadow: 0 0 0 0 rgba(34, 197, 94, 0); }
}

@media (max-width: 1200px) {
  .chat-scroll {
    height: auto;
    max-height: 620px;
  }

  .side-rail {
    position: static;
  }
}

@media (max-width: 768px) {
  .agent-hero,
  .composer-actions,
  .feedback-bar {
    grid-template-columns: 1fr;
    flex-direction: column;
    align-items: stretch;
  }

  .settings-line,
  .status-grid {
    grid-template-columns: 1fr;
  }

  .bubble {
    max-width: 88%;
  }
}
</style>
